/**
 * 3D bottle carousel — matches Ahmed Al Maghribi .carousel-container-3d
 * (spring layout, 3-face bottles, drag, gold glow).
 */
(function () {
  const MQ = 768;
  const DRAG_THRESHOLD = 60;
  const DRAG_START = 16;

  class ZaoudCarousel3d {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-c3d-item]'));
      this.bgs = Array.from(root.querySelectorAll('[data-c3d-bg]'));
      this.years = Array.from(root.querySelectorAll('[data-c3d-year]'));
      this.prevBtn = root.querySelector('[data-c3d-prev]');
      this.nextBtn = root.querySelector('[data-c3d-next]');
      this.count = this.items.length;
      if (this.count === 0) return;

      const initial = parseInt(root.dataset.initialIndex, 10);
      this.index = Number.isFinite(initial) ? ((initial % this.count) + this.count) % this.count : 0;
      this.mobile = window.innerWidth < MQ;
      this.dragX = 0;
      this.pointerId = null;
      this.moved = false;
      this.startX = 0;
      this.capturing = false;

      this.onResize = this.onResize.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.prevBtn && this.prevBtn.addEventListener('click', this.onPrev);
      this.nextBtn && this.nextBtn.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize);

      this.items.forEach((item) => {
        item.addEventListener('pointerdown', this.onPointerDown);
        item.addEventListener('click', this.onClickCapture, true);
      });

      this.render();
    }

    destroy() {
      this.prevBtn && this.prevBtn.removeEventListener('click', this.onPrev);
      this.nextBtn && this.nextBtn.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      this.items.forEach((item) => {
        item.removeEventListener('pointerdown', this.onPointerDown);
        item.removeEventListener('pointermove', this.onPointerMove);
        item.removeEventListener('pointerup', this.onPointerUp);
        item.removeEventListener('pointercancel', this.onPointerUp);
        item.removeEventListener('click', this.onClickCapture, true);
      });
    }

    onResize() {
      const next = window.innerWidth < MQ;
      if (next === this.mobile) return;
      this.mobile = next;
      this.render();
    }

    onPrev(event) {
      event.preventDefault();
      this.go(this.index - 1);
    }

    onNext(event) {
      event.preventDefault();
      this.go(this.index + 1);
    }

    go(index) {
      if (this.count < 2) return;
      this.index = ((index % this.count) + this.count) % this.count;
      this.dragX = 0;
      this.render();
    }

    slotFor(i) {
      if (i === this.index) return 'center';
      if ((this.index + 1) % this.count === i) return 'right';
      return 'left';
    }

    pose(slot) {
      const x = this.mobile ? 120 : 300;
      if (slot === 'center') {
        return {
          x: 0,
          z: 0,
          rotateY: 0,
          scale: this.mobile ? 1.1 : 1.3,
          opacity: 1,
          zIndex: 10,
        };
      }
      if (slot === 'left') {
        return { x: -x, z: -300, rotateY: 15, scale: 0.8, opacity: 0.4, zIndex: 5 };
      }
      return { x: x, z: -300, rotateY: -15, scale: 0.8, opacity: 0.4, zIndex: 5 };
    }

    render() {
      this.items.forEach((item, i) => {
        const slot = this.slotFor(i);
        const pose = this.pose(slot);
        const active = slot === 'center';
        item.classList.toggle('active', active);
        item.style.setProperty('--c3d-x', pose.x + 'px');
        item.style.setProperty('--c3d-z', pose.z + 'px');
        item.style.setProperty('--c3d-ry', pose.rotateY + 'deg');
        item.style.setProperty('--c3d-scale', String(pose.scale));
        item.style.setProperty('--c3d-opacity', String(pose.opacity));
        item.style.setProperty('--c3d-z-index', String(pose.zIndex));
        item.style.setProperty('--c3d-drag', (active ? this.dragX : 0) + 'px');

        item.querySelectorAll('[data-c3d-face]').forEach((face) => {
          face.classList.toggle('is-visible', face.dataset.c3dFace === slot);
        });
      });

      this.bgs.forEach((bg, i) => bg.classList.toggle('is-active', i === this.index));
      this.years.forEach((year, i) => year.classList.toggle('is-active', i === this.index));
    }

    onPointerDown(event) {
      const item = event.currentTarget;
      if (!item.classList.contains('active')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.dragX = 0;
      this.moved = false;
      this.capturing = false;
      item.addEventListener('pointermove', this.onPointerMove);
      item.addEventListener('pointerup', this.onPointerUp);
      item.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const dx = event.clientX - this.startX;
      if (Math.abs(dx) < DRAG_START) return;

      const item = event.currentTarget;
      if (!this.capturing) {
        this.capturing = true;
        item.classList.add('is-dragging');
        try {
          item.setPointerCapture(event.pointerId);
        } catch (err) {
          /* capture is optional */
        }
      }

      if (Math.abs(dx) > DRAG_THRESHOLD) this.moved = true;
      this.dragX = dx;
      item.style.setProperty('--c3d-drag', dx + 'px');
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      const item = event.currentTarget;
      const dx = this.dragX;
      item.classList.remove('is-dragging');
      if (this.capturing) {
        try {
          item.releasePointerCapture(event.pointerId);
        } catch (err) {
          /* already released */
        }
      }
      item.removeEventListener('pointermove', this.onPointerMove);
      item.removeEventListener('pointerup', this.onPointerUp);
      item.removeEventListener('pointercancel', this.onPointerUp);
      this.pointerId = null;
      this.capturing = false;

      if (dx < -DRAG_THRESHOLD) {
        this.moved = true;
        this.go(this.index + 1);
      } else if (dx > DRAG_THRESHOLD) {
        this.moved = true;
        this.go(this.index - 1);
      } else {
        this.dragX = 0;
        item.style.setProperty('--c3d-drag', '0px');
      }
    }

    onClickCapture(event) {
      const item = event.currentTarget;
      if (!item.classList.contains('active')) {
        event.preventDefault();
        event.stopPropagation();
        const index = this.items.indexOf(item);
        if (index >= 0) this.go(index);
        this.moved = false;
        return;
      }

      if (this.moved) {
        event.preventDefault();
        event.stopPropagation();
        this.moved = false;
      }
    }
  }

  const instances = new WeakMap();

  function boot(scope) {
    const roots = scope.querySelectorAll ? scope.querySelectorAll('[data-zaoud-c3d]') : [];
    roots.forEach((root) => {
      const prev = instances.get(root);
      if (prev) {
        prev.destroy();
        instances.delete(root);
      }
      const carousel = new ZaoudCarousel3d(root);
      if (carousel.count) instances.set(root, carousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-zaoud-c3d]').forEach((root) => {
      const carousel = instances.get(root);
      if (carousel) {
        carousel.destroy();
        instances.delete(root);
      }
    });
  });
})();
